import { useTranslation } from "react-i18next";
import { Clock, AlertCircle } from "lucide-react";
import { cn } from "@tabipla/ui";
import { useDeadlineCountdown } from "../../application/hooks/useDeadlineCountdown";

interface CountdownBadgeProps {
  deadline: string | null | undefined;
  className?: string;
  showIcon?: boolean;
  variant?: "default" | "compact";
}

export function CountdownBadge({
  deadline,
  className,
  showIcon = true,
  variant = "default",
}: CountdownBadgeProps) {
  const { t } = useTranslation();
  const countdown = useDeadlineCountdown(deadline);

  if (!countdown || !deadline) return null;

  const getDisplayText = () => {
    if (countdown.isExpired) return t("polls.expired");

    if (countdown.days > 0) {
      return t("polls.expiresInDays", { days: countdown.days });
    }

    if (countdown.hours > 0) {
      return t("polls.expiresInHours", { hours: countdown.hours });
    }

    if (countdown.minutes > 0) {
      return t("polls.expiresInMinutes", { minutes: countdown.minutes });
    }

    // Less than 1 minute
    return t("countdown.seconds", { seconds: countdown.seconds });
  };

  const Icon = countdown.isUrgent || countdown.isExpired ? AlertCircle : Clock;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        // Default (no urgency)
        !countdown.isExpired &&
          !countdown.isUrgent &&
          !countdown.isWarning &&
          "bg-muted text-muted-foreground",
        // Warning (< 24h)
        !countdown.isExpired &&
          !countdown.isUrgent &&
          countdown.isWarning &&
          "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
        // Urgent (< 1h)
        !countdown.isExpired &&
          countdown.isUrgent &&
          "animate-pulse bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
        // Expired
        countdown.isExpired && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
        variant === "compact" && "px-1.5 py-0",
        className
      )}
    >
      {showIcon && <Icon className={cn("h-3 w-3", countdown.isUrgent && "animate-pulse")} />}
      <span className={countdown.isUrgent ? "tabular-nums" : ""}>{getDisplayText()}</span>
    </span>
  );
}
